class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this.querySelector('#cart-drawer');
    this.overlay = document.querySelector('#cart-drawer-overlay');
    this.closeButton = this.querySelector('.cart-drawer__close');
    this.cartItems = this.querySelector('[data-cart-items]');
    this.cartCount = document.querySelector('[data-cart-count]');
    this.subtotalElement = this.querySelector('.cart-drawer__subtotal .price');
    this.originalBodyOverflow = null;

    this.bindEvents();
    this.init();
  }

  init() {
    // Initialize cart count
    if (this.cartCount) {
      const currentCount = this.dataset.cartItemCount || '0';
      this.cartCount.textContent = currentCount;
    }
    
    // Ensure the drawer is initially hidden
    if (this.drawer) {
      this.drawer.classList.remove('is-open');
    }
    if (this.overlay) {
      this.overlay.classList.remove('is-open');
    }
  }

  bindEvents() {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });

    // Handle quantity changes
    this.querySelectorAll('.quantity__input').forEach(input => {
      input.addEventListener('change', (event) => this.updateItemQuantity(event));
    });

    // Handle quantity buttons
    this.querySelectorAll('[data-quantity-minus]').forEach(button => {
      button.addEventListener('click', (event) => {
        const input = event.target.closest('.quantity').querySelector('.quantity__input');
        const currentValue = parseInt(input.value);
        if (currentValue > 1) {
          input.value = currentValue - 1;
          input.dispatchEvent(new Event('change'));
        }
      });
    });

    this.querySelectorAll('[data-quantity-plus]').forEach(button => {
      button.addEventListener('click', (event) => {
        const input = event.target.closest('.quantity').querySelector('.quantity__input');
        const currentValue = parseInt(input.value);
        input.value = currentValue + 1;
        input.dispatchEvent(new Event('change'));
      });
    });

    // Handle remove buttons
    this.querySelectorAll('.cart-drawer__item-remove').forEach(button => {
      button.addEventListener('click', (event) => this.removeItem(event));
    });
  }

  open() {
    console.log('Opening cart drawer'); // Debug log
    
    if (this.drawer) {
      // Store original overflow value
      this.originalBodyOverflow = document.body.style.overflow || getComputedStyle(document.body).overflow;
      
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      
      // Show overlay
      if (this.overlay) {
        this.overlay.classList.add('is-open');
      }
      
      // Show drawer
      this.drawer.classList.add('is-open');
      
      console.log('Cart drawer should be visible now'); // Debug log
    }
  }

  close() {
    console.log('Closing cart drawer'); // Debug log
    
    if (this.drawer) {
      this.drawer.classList.remove('is-open');
      
      // Hide overlay
      if (this.overlay) {
        this.overlay.classList.remove('is-open');
      }
      
      // Restore original overflow value
      if (this.originalBodyOverflow && this.originalBodyOverflow !== 'hidden') {
        document.body.style.overflow = this.originalBodyOverflow;
      } else {
        document.body.style.removeProperty('overflow');
      }
    }
  }

  async updateItemQuantity(event) {
    const input = event.target;
    const itemKey = input.closest('[data-cart-item-key]').dataset.cartItemKey;
    const quantity = parseInt(input.value);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: quantity
        })
      });

      const cart = await response.json();
      this.updateCart(cart);
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async removeItem(event) {
    const button = event.target.closest('.cart-drawer__item-remove');
    const itemKey = button.closest('[data-cart-item-key]').dataset.cartItemKey;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });

      const cart = await response.json();
      this.updateCart(cart);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  updateCart(cart) {
    // Update cart count
    if (this.cartCount) {
      this.cartCount.textContent = cart.item_count;
    }

    // Update cart items
    if (cart.item_count === 0) {
      this.cartItems.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your cart is empty</p>
          <a href="/collections/all" class="button">
            Continue shopping
          </a>
        </div>
      `;
    } else {
      // Update existing items
      cart.items.forEach(item => {
        const itemElement = this.querySelector(`[data-cart-item-key="${item.key}"]`);
        if (itemElement) {
          const quantityInput = itemElement.querySelector('.quantity__input');
          if (quantityInput) {
            quantityInput.value = item.quantity;
          }
        }
      });
    }

    // Update subtotal
    if (this.subtotalElement) {
      this.subtotalElement.textContent = this.formatMoney(cart.total_price);
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  // Method to render cart contents (called by product-form.js)
  renderContents(response) {
    console.log('Cart drawer renderContents called', response); // Debug log
    
    // Update cart count
    if (this.cartCount && response.item_count !== undefined) {
      this.cartCount.textContent = response.item_count;
    }

    // Open the drawer when an item is added
    this.open();
  }

  // Method to get sections to render (required by product-form.js)
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer'
      }
    ];
  }

  // Method to set active element (required by product-form.js)
  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
